import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * Durable browser storage (IndexedDB):
 * - outbox: operations waiting to be sent to the server, in capture order
 * - local: entries captured on this device (shown as "Saved locally" until the server has them)
 * - cache: last known server data so the app can be read offline
 * Browser storage is a convenience, not a backup; the server is the source of truth.
 */
export interface OutboxOp {
  seq?: number;
  opId: string;
  type: 'createSession' | 'createEntry' | 'editEntry' | 'deleteEntry';
  payload: any;
  campaignId: string;
  createdAt: string;
  attempts: number;
  error?: string;
  failed?: boolean;
}

export interface LocalEntry {
  id: string;
  campaignId: string;
  sessionId: string;
  text: string;
  revisionId: string;
  capturedAt: string;
  capturedTz: string;
  clientSeq: number;
  visibility: 'public' | 'gm_only' | null;
  inGameTime: string | null;
  synced: boolean;
  syncedAt?: string;
}

interface Schema extends DBSchema {
  outbox: { key: number; value: OutboxOp; indexes: { opId: string } };
  local: { key: string; value: LocalEntry; indexes: { session: string } };
  cache: { key: string; value: { key: string; data: any; savedAt: string } };
}

let dbp: Promise<IDBPDatabase<Schema>> | null = null;

export function db(): Promise<IDBPDatabase<Schema>> {
  if (!dbp) {
    dbp = openDB<Schema>('trpg-notes', 1, {
      upgrade(d) {
        const o = d.createObjectStore('outbox', { keyPath: 'seq', autoIncrement: true });
        o.createIndex('opId', 'opId', { unique: true });
        const l = d.createObjectStore('local', { keyPath: 'id' });
        l.createIndex('session', 'sessionId');
        d.createObjectStore('cache', { keyPath: 'key' });
      },
    });
  }
  return dbp;
}

export async function cacheGet<T>(key: string): Promise<T | undefined> {
  try {
    return (await (await db()).get('cache', key))?.data as T | undefined;
  } catch {
    return undefined;
  }
}

export async function cacheSet(key: string, data: unknown): Promise<void> {
  try {
    await (await db()).put('cache', { key, data, savedAt: new Date().toISOString() });
  } catch {
    /* cache is best effort */
  }
}

/** Writes the local entry and its outbox op in one IndexedDB transaction. */
export async function saveCapture(entry: LocalEntry, op: OutboxOp): Promise<void> {
  const d = await db();
  const tx = d.transaction(['local', 'outbox'], 'readwrite');
  await Promise.all([tx.objectStore('local').put(entry), tx.objectStore('outbox').add(op), tx.done]);
}

export async function addOp(op: OutboxOp): Promise<void> {
  await (await db()).add('outbox', op);
}

export async function listOps(): Promise<OutboxOp[]> {
  return (await db()).getAll('outbox');
}

export async function localEntries(sessionId: string): Promise<LocalEntry[]> {
  return (await db()).getAllFromIndex('local', 'session', sessionId);
}

export async function markSynced(entryId: string): Promise<void> {
  const d = await db();
  const e = await d.get('local', entryId);
  if (e && !e.synced) await d.put('local', { ...e, synced: true, syncedAt: new Date().toISOString() });
}

/** Drop local copies the server has had for a while (the cache holds server data). */
export async function pruneLocal(maxAgeMs = 7 * 86400_000): Promise<void> {
  const d = await db();
  const all = await d.getAll('local');
  const cutoff = Date.now() - maxAgeMs;
  for (const e of all) if (e.synced && e.syncedAt && Date.parse(e.syncedAt) < cutoff) await d.delete('local', e.id);
}
